import * as React from 'react';
import Form from 'antd/lib/form';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import Button from 'antd/lib/button';
import { antGrey, runwayColors } from '../../styles/globalStyles';
import { Input, Radio } from 'antd/lib';
import { IntegrationType } from '@/types';
import type { Nullable } from '@/typescriptHelpers';
import SupportedTokensComponent from '../../common/SupportedTokensComponent';
import { TestCasesFilterSettings } from './TestCasesFilterSettings';

type TestRunsFilterSettingsProps = {
    rootFieldName: string | number;
    parentPath?: (string | number)[];
    integrationType: Nullable<IntegrationType>;
};

export const TestRunsFilterSettings = ({ rootFieldName, parentPath, integrationType }: TestRunsFilterSettingsProps) => {
    const supportsTestCaseFilters = integrationType === IntegrationType.BrowserStack;
    return (
        <Form.List name={parentPath ?? rootFieldName} initialValue={[{}]}>
            {(fields, { add, remove }) => (
                <Space direction="vertical" style={{ width: '100%' }}>
                    {fields.map((field, index) => {
                        return (
                            <div
                                key={field.key}
                                style={{
                                    position: 'relative',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    padding: 20,
                                    borderRadius: 8,
                                    marginBottom: 16,
                                    marginLeft: parentPath ? 16 : 0,
                                    border: `1px solid ${antGrey.grey5}`,
                                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                }}
                            >
                                <Text strong style={{ paddingBottom: 2 }}>
                                    Test run - pattern {index + 1}
                                </Text>
                                {index > 0 && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            right: 0,
                                            paddingRight: 14,
                                            paddingTop: 8,
                                        }}
                                    >
                                        <Button
                                            icon={<DeleteOutlined style={{ color: runwayColors.statusRed }} />}
                                            danger
                                            type="link"
                                            onClick={() => remove(field.name)}
                                        />
                                    </div>
                                )}
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0,
                                        marginTop: 6,
                                    }}
                                >
                                    <Form.Item
                                        name={[field.name, 'pattern']}
                                        rules={[{ required: true, message: 'Please add a pattern' }]}
                                        extra={<SupportedTokensComponent />}
                                        style={{ marginBottom: supportsTestCaseFilters ? 16 : 0 }}
                                    >
                                        <Input
                                            allowClear
                                            style={{ width: '100%' }}
                                            placeholder="{appPlatform} {version} test run"
                                        />
                                    </Form.Item>

                                    {supportsTestCaseFilters && (
                                        <>
                                            <Form.Item
                                                initialValue={false}
                                                rules={[{ required: true, message: 'Please select an option' }]}
                                                name={[field.name, 'shouldUseTestCaseFilters']}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Radio.Group>
                                                    <Radio value={false}>Clone test cases from previous version</Radio>
                                                    <Radio value={true}>Specify test case filters</Radio>
                                                </Radio.Group>
                                            </Form.Item>

                                            <Form.Item noStyle shouldUpdate>
                                                {(form) => {
                                                    const shouldUseTestCaseFilters = form.getFieldValue([
                                                        rootFieldName,
                                                        ...(parentPath ?? []),
                                                        field.name,
                                                        'shouldUseTestCaseFilters',
                                                    ]);

                                                    if (!shouldUseTestCaseFilters) {
                                                        return null;
                                                    }

                                                    return (
                                                        <div style={{ marginTop: 16 }}>
                                                            <TestCasesFilterSettings
                                                                parentFieldPath={[field.name, 'testCaseFilters']}
                                                                integrationType={integrationType}
                                                            />
                                                        </div>
                                                    );
                                                }}
                                            </Form.Item>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    <Button icon={<PlusOutlined style={{ color: runwayColors.primary }} />} onClick={() => add()}>
                        Add another test run pattern
                    </Button>
                </Space>
            )}
        </Form.List>
    );
};
